import { ref } from 'vue'
import type { KeyConfig } from '@rapoo/element'

type TooltipReason = 'hover' | 'auto' | null

export const useKeyTooltip = (isModified: (keyCode: string) => boolean) => {
  // 当前需要触发tooltip的按键
  const activeTooltipKeyCode = ref<string | null>(null)
  // 触发tooltip的原因，hover表示鼠标悬浮，auto表示自动触发（如拖拽结束后）
  const activeTooltipReason = ref<TooltipReason>(null)
  // 自动触发原因下需要自动关闭定时器
  let tooltipTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * 清理 tooltip 自动关闭定时器。
   */
  const clearTooltipTimer = () => {
    if (!tooltipTimer) return
    clearTimeout(tooltipTimer)
    tooltipTimer = null
  }

  /**
   * 自动打开 tooltip，并在 2 秒后自动关闭。
   * 如果中途被 hover 接管，则不会被自动关闭逻辑误关。
   */
  const openTooltipWithAuto = (code: string) => {
    activeTooltipKeyCode.value = code
    activeTooltipReason.value = 'auto'
    clearTooltipTimer()

    tooltipTimer = setTimeout(() => {
      if (activeTooltipReason.value !== 'auto') return
      if (activeTooltipKeyCode.value !== code) return

      activeTooltipKeyCode.value = null
      activeTooltipReason.value = null
      tooltipTimer = null
    }, 2000)
  }

  /**
   * 由 hover 行为直接打开 tooltip。
   */
  const openTooltipWithHover = (code: string) => {
    clearTooltipTimer()
    activeTooltipKeyCode.value = code
    activeTooltipReason.value = 'hover'
  }

  /**
   * 同步目标按键 tooltip 的开关状态（hover 驱动）。
   */
  const handleTooltipOpen = (open: boolean, key: KeyConfig) => {
    const code = key.keywords
    if (!isModified(code)) return

    if (open) {
      openTooltipWithHover(code)
      return
    }

    if (activeTooltipReason.value !== 'hover') return
    if (activeTooltipKeyCode.value !== code) return

    activeTooltipKeyCode.value = null
    activeTooltipReason.value = null
  }

  /**
   * 按目标键清理 tooltip（用于恢复改键等场景）。
   */
  const clearTooltip = (code: string) => {
    if (activeTooltipKeyCode.value !== code) return

    activeTooltipKeyCode.value = null
    activeTooltipReason.value = null

    clearTooltipTimer()
  }

  return {
    activeTooltipKeyCode,
    clearTooltipTimer,
    openTooltipWithAuto,
    openTooltipWithHover,
    handleTooltipOpen,
    clearTooltip,
  }
}
